import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { BarChart3, Mail, Eye, MousePointer2, AlertTriangle, UserMinus, ExternalLink, RefreshCw, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const StatPill = ({ icon: Icon, label, value, sub, tone = 'indigo', testid }) => {
  const toneMap = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <Card className={`border ${toneMap[tone]}`} data-testid={testid}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
          <Icon size={14} /> {label}
        </div>
        <div className="text-3xl font-bold mt-1">{value}</div>
        {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const CampaignDetailView = ({ campaign, onBack }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/campaigns/${campaign.id}/stats`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load stats');
      setStats(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load campaign stats');
    } finally {
      setLoading(false);
    }
  }, [campaign.id]);

  useEffect(() => { load(); }, [load]);

  if (loading || !stats) {
    return (
      <div className="text-center py-12" data-testid="campaign-stats-loading">
        <Loader2 size={24} className="animate-spin mx-auto text-indigo-600" />
        <p className="text-sm text-gray-500 mt-2">Loading campaign analytics…</p>
      </div>
    );
  }

  const s = stats.stats || {};
  return (
    <div className="space-y-4" data-testid="campaign-detail-view">
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={onBack} data-testid="back-to-overview">
          <ChevronLeft size={16} className="mr-1" /> Back to Overview
        </Button>
        <Button size="sm" variant="outline" onClick={load} data-testid="refresh-stats">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      <div>
        <h3 className="text-xl font-bold text-slate-900">{stats.campaign_name}</h3>
        <p className="text-sm text-gray-500">{stats.subject}</p>
        <p className="text-xs text-gray-400 mt-1">Sent to <strong>{stats.sent_count}</strong> recipients</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill icon={Mail}           label="Delivered"   value={s.delivered}              tone="emerald" testid="stat-delivered" />
        <StatPill icon={Eye}            label="Opens"       value={s.opens}       sub={`${s.open_rate_pct}% open rate`}  tone="indigo"  testid="stat-opens" />
        <StatPill icon={MousePointer2}  label="Clicks"      value={s.clicks}      sub={`${s.click_rate_pct}% click rate`} tone="indigo"  testid="stat-clicks" />
        <StatPill icon={AlertTriangle}  label="Bounces"     value={s.bounces}     sub={`${s.bounce_rate_pct}% bounce rate`} tone="red"     testid="stat-bounces" />
        <StatPill icon={UserMinus}      label="Unsubscribed" value={s.unsubscribes} sub={`${s.unsubscribe_rate_pct}%`}     tone="amber"   testid="stat-unsubs" />
        <StatPill icon={AlertTriangle}  label="Spam Reports" value={s.spam_reports}                                     tone="red"     testid="stat-spam" />
        <StatPill icon={AlertTriangle}  label="Dropped"     value={s.dropped}                                           tone="gray"    testid="stat-dropped" />
        <StatPill icon={Mail}           label="Processed"   value={s.processed}                                         tone="gray"    testid="stat-processed" />
      </div>

      {/* Top Clicked Links */}
      <Card>
        <CardContent className="pt-5">
          <h4 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
            <ExternalLink size={16} className="text-indigo-600" />Top Clicked Links
          </h4>
          {stats.top_links.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No clicks recorded yet.</p>
          ) : (
            <ul className="divide-y" data-testid="top-links-list">
              {stats.top_links.map((row, i) => (
                <li key={i} className="py-2 flex items-center justify-between gap-3">
                  <a href={row.url} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline truncate flex-1">
                    {row.url}
                  </a>
                  <div className="flex gap-3 text-xs text-gray-500 shrink-0">
                    <span><strong className="text-slate-900">{row.clicks}</strong> clicks</span>
                    <span><strong className="text-slate-900">{row.unique_emails}</strong> unique</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Recent Events */}
      <Card>
        <CardContent className="pt-5">
          <h4 className="font-semibold text-sm text-slate-900 mb-3">Recent Events</h4>
          {stats.recent_events.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No events yet. Once recipients open / click / bounce, they'll show here.</p>
          ) : (
            <ul className="divide-y text-xs" data-testid="recent-events-list">
              {stats.recent_events.map((ev, i) => (
                <li key={i} className="py-2 flex items-center gap-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                    ev.event === 'open' ? 'bg-indigo-100 text-indigo-700'
                    : ev.event === 'click' ? 'bg-emerald-100 text-emerald-700'
                    : ev.event === 'bounce' ? 'bg-red-100 text-red-700'
                    : ev.event === 'delivered' ? 'bg-green-100 text-green-700'
                    : ev.event === 'unsubscribe' ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-700'
                  }`}>{ev.event}</span>
                  <span className="text-slate-900 truncate flex-1">{ev.email}</span>
                  {ev.url && <a href={ev.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline truncate max-w-[200px]">{ev.url}</a>}
                  {ev.reason && <span className="text-red-600 italic">{ev.reason}</span>}
                  <span className="text-gray-400 whitespace-nowrap">{ev.received_at ? new Date(ev.received_at).toLocaleString() : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CRMCampaignAnalytics = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, oRes] = await Promise.all([
        fetch(`${API}/api/crm/campaigns`, { credentials: 'include' }),
        fetch(`${API}/api/crm/campaigns/overview-stats`, { credentials: 'include' }),
      ]);
      const cData = await cRes.json();
      const oData = await oRes.json();
      const list = (Array.isArray(cData) ? cData : (cData.campaigns || []))
        .filter(c => !c.is_opt_in_template && c.sent_at);
      setCampaigns(list);
      setOverview(oData);
    } catch (err) {
      toast.error(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selected) return <CampaignDetailView campaign={selected} onBack={() => { setSelected(null); load(); }} />;

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="analytics-loading">
        <Loader2 size={24} className="animate-spin mx-auto text-indigo-600" />
        <p className="text-sm text-gray-500 mt-2">Loading analytics…</p>
      </div>
    );
  }

  const t = overview?.totals || {};
  const perCampaignMap = Object.fromEntries((overview?.per_campaign || []).map(p => [p.campaign_id, p]));

  return (
    <div className="space-y-4" data-testid="campaign-analytics-view">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 size={18} className="text-indigo-600" /> Campaign Analytics
        </h3>
        <Button size="sm" variant="outline" onClick={load} data-testid="refresh-overview">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Global totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatPill icon={Mail}           label="Delivered"   value={t.delivered || 0}   tone="emerald" testid="total-delivered" />
        <StatPill icon={Eye}            label="Opens"       value={t.open || 0}        tone="indigo"  testid="total-opens" />
        <StatPill icon={MousePointer2}  label="Clicks"      value={t.click || 0}       tone="indigo"  testid="total-clicks" />
        <StatPill icon={AlertTriangle}  label="Bounces"     value={t.bounce || 0}      tone="red"     testid="total-bounces" />
        <StatPill icon={UserMinus}      label="Unsubscribes" value={t.unsubscribe || 0} tone="amber"   testid="total-unsubs" />
      </div>

      {/* Per-campaign list */}
      <Card>
        <CardContent className="pt-5">
          <h4 className="font-semibold text-sm text-slate-900 mb-3">Sent Campaigns</h4>
          {campaigns.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No campaigns have been sent yet. Once you send a campaign, it'll appear here with its stats.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="campaigns-stats-table">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                    <th className="py-2 pr-3">Campaign</th>
                    <th className="py-2 px-3 text-center">Sent</th>
                    <th className="py-2 px-3 text-center">Opens</th>
                    <th className="py-2 px-3 text-center">Clicks</th>
                    <th className="py-2 px-3 text-center">Bounces</th>
                    <th className="py-2 px-3 text-center">Unsubs</th>
                    <th className="py-2 pl-3 text-center">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(c => {
                    const stat = perCampaignMap[c.id] || {};
                    return (
                      <tr key={c.id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`campaign-row-${c.id}`}>
                        <td className="py-3 pr-3">
                          <div className="font-medium text-slate-900">{c.name}</div>
                          <div className="text-xs text-gray-500">{c.subject}</div>
                          <div className="text-xs text-gray-400">{c.sent_at ? new Date(c.sent_at).toLocaleString() : ''}</div>
                        </td>
                        <td className="py-3 px-3 text-center font-medium">{c.recipient_count || c.sent_count || 0}</td>
                        <td className="py-3 px-3 text-center">{stat.open || 0}</td>
                        <td className="py-3 px-3 text-center">{stat.click || 0}</td>
                        <td className="py-3 px-3 text-center text-red-600">{stat.bounce || 0}</td>
                        <td className="py-3 px-3 text-center text-amber-600">{stat.unsubscribe || 0}</td>
                        <td className="py-3 pl-3 text-center">
                          <Button size="sm" variant="outline" onClick={() => setSelected(c)} data-testid={`view-stats-${c.id}`}>
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CRMCampaignAnalytics;
